import { Component, OnInit, ViewChild, HostListener, inject } from '@angular/core';
import { NgStyle } from '@angular/common';
import { Router } from '@angular/router';

import { UserService, UserHeaderInfo } from './../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { MenuService } from '../../services/menu.service';
import { NotificationService } from '../../services/notification.service';
import { DialogManagerService } from '../../services/dialog-manager.service';
import { ProfileImageService } from '../../services/profile-image.service';

import { finalize, switchMap } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { MenubarModule } from 'primeng/menubar';
import { MenuItem } from 'primeng/api';
import { TieredMenuModule, TieredMenu } from 'primeng/tieredmenu';
import {
  mayAdministerMeetings,
  mayViewDocentTimetable,
} from 'src/app/utils/meeting-access';
import { DialogModule } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';

import { user } from '../../models/user.model';
import { ActivityService } from 'src/app/services/activity.service';
import { UpdateUserdataDialogComponent } from '../Dialogs/update-userdata-dialog/update-userdata-dialog.component';
import { UpdatePersonalInformationDTO } from 'src/app/models/update-user/update-personal-information.model';
import {
  DEFAULT_AVATAR,
  ProfileImageEditorComponent,
} from '../profile-image-editor/profile-image-editor.component';
import { INVENTORY_PERMISSIONS } from '../menu/inventory/inventory-permissions';
import { AvatarBadge, initialsAndColor } from '../../utils/avatar';
import { mayUseStudentView } from '../../utils/view-access';

// Muss mit $bp-mobile in navbar.component.scss übereinstimmen
const MOBILE_BREAKPOINT = 820;

// Beschreibt einen Eintrag des Zahnrad-Menüs, bevor daraus ein MenuItem wird.
interface SettingsMenuEntry {
  label: string;
  icon: string;
  routerLink: string;
  // Benötigte Berechtigung; ohne Angabe ist der Eintrag immer sichtbar
  permission?: string;
  // Überschreibt die Berechtigungsprüfung für Sonderfälle
  visible?: boolean;
}

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    NgStyle,
    MenubarModule,
    TieredMenuModule,
    DialogModule,
    ButtonModule,
    SkeletonModule,
    ProfileImageEditorComponent,
    UpdateUserdataDialogComponent,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnInit {
  @ViewChild('mobileMenu') mobileMenu!: TieredMenu;
  @ViewChild('profileMenu') profileMenu!: TieredMenu;

  // Fürs Template: entscheidet Initialen vs. <img>
  readonly DEFAULT_AVATAR = DEFAULT_AVATAR;

  permissions: string[] = [];
  roleId = 0;

  displayRegisterDialog = false;

  // Steuert, ob der Dialog "Persönliche Daten" schon befüllt werden kann. Der Kind-Dialog
  // baut sein Formular in ngOnInit auf und darf deshalb nicht vor den Daten entstehen.
  userDataLoaded = false;

  savingUserData = false;

  userData: user = {
    userId: 0,
    firstname: '',
    lastname: '',
    email: '',
    roles: [],
    residences: [],
    notifications: true,
  };

  // userData.profileImageUrl ist ein MinIO-Object-Key, keine anzeigbare Adresse. Fürs
  // Anzeigen wird daraus eine presigned URL aufgelöst, die nach 24h abläuft und deshalb
  // bewusst nicht gespeichert wird.
  //
  // null = noch nicht aufgelöst -> Platzhalter. Erst wenn hier etwas steht, wird ein Bild
  // gezeigt; so springt die Navbar nicht von einem fremden Default-Avatar auf das echte um.
  profileImageDisplayUrl: string | null = null;

  // Initialen-Kreis, solange kein eigenes Profilbild hinterlegt ist
  get avatarBadge(): AvatarBadge {
    return initialsAndColor(
      `${this.userData.firstname} ${this.userData.lastname}`.trim(),
    );
  }

  menuItems: MenuItem[] = [];
  settingsMenuItems: MenuItem[] = [];

  // Platzhalterzeilen des Formulars, solange die Benutzerdaten noch nicht da sind
  readonly formSkeletonRows = [1, 2, 3, 4, 5, 6];

  // Getter statt Feld: bei Feldinitialisierern ist authService noch nicht zugewiesen.
  private get profileMenuItemsConst(): MenuItem[] {
    return [
      {
        label: 'Persönliche Daten',
        icon: 'pi pi-id-card',
        command: () => this.showRegisterDialog(),
      },
      {
        label: 'Passwort ändern',
        icon: 'pi pi-key',
        command: () =>
          this.dialogManager.openChangePasswordDialog(this.authService.getUserEmail()),
      },
      {
        // Für die Verwaltung liegt dieselbe Route zusätzlich unter 'Dozenten-Stundenpläne'
        // im Einstellungsmenü — dort zur Auswahl eines fremden Plans.
        label: 'Mein Stundenplan',
        icon: 'pi pi-calendar',
        routerLink: '/docent-timetable',
        // Muss mit docentTimetableGuard in menu.routes.ts übereinstimmen, sonst führt der
        // Eintrag ins Leere.
        visible: mayViewDocentTimetable(this.authService),
      },
      {
        label: 'Teilnehmer-Ansicht',
        icon: 'pi pi-arrow-right-arrow-left',
        routerLink: '/student',
        visible: mayUseStudentView(this.authService),
      },
      { separator: true },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => this.logout(),
      },
    ];
  }

  // Vorbelegt, damit Logout auch dann erreichbar bleibt, wenn die Benutzerdaten nicht laden.
  // Erst in der Konstruktor-Zuweisung (statt als Feldinitialisierer) gesetzt, weil
  // profileMenuItemsConst über authService liest, das als Parameter-Property erst im
  // Konstruktor-Rumpf zugewiesen wird - vor Feldinitialisierern wäre es noch undefined.
  profileMenuItems: MenuItem[];

  private dialogManager = inject(DialogManagerService);
  private profileImageService = inject(ProfileImageService);

  constructor(
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router,
    private menuService: MenuService,
    private userService: UserService,
    private activityService: ActivityService,
  ) {
    this.profileMenuItems = [...this.profileMenuItemsConst];
  }

  ngOnInit(): void {
    this.permissions = this.authService.getUserPermissions();
    this.roleId = this.authService.getUserRole();

    // Der Name steht bereits im JWT - dafür braucht es keinen Request
    this.buildProfileMenu(this.authService.getUserName());

    const userId = this.authService.getCurrentUserId();
    if (userId) {
      // Vorab aus dem JWT: das Profilbild lässt sich damit auch dann noch ändern,
      // wenn der folgende Request scheitert.
      this.userData = { ...this.userData, userId };

      // Bewusst der schmale Endpunkt statt getUserDetails: die Navbar braucht nur den
      // Bild-Key, nicht den User samt aller Aktivitäten. Die vollen Daten lädt erst
      // showRegisterDialog() nach.
      this.userService.getUserById(userId).subscribe({
        next: (info) => {
          this.applyHeaderInfo(info);
          this.resolveProfileImage(info.profileImageUrl);
        },
        error: (err) => {
          // Ohne Auflösung bliebe der Platzhalter dauerhaft stehen
          this.profileImageDisplayUrl = DEFAULT_AVATAR;
          this.notificationService.error(
            'Benutzerdaten konnten nicht geladen werden',
            err,
          );
        },
      });
    }

    if (this.roleId) {
      this.menuItems = this.menuService.getMenuItems(this.permissions);
      this.settingsMenuItems = this.buildSettingsMenu();
    }
  }

  // Schließt das mobile Menü, sobald wieder die Desktop-Navigation sichtbar wird
  @HostListener('window:resize')
  onWindowResize(): void {
    if (window.innerWidth > MOBILE_BREAKPOINT) {
      this.mobileMenu?.hide();
    }
  }

  private resolveProfileImage(key?: string | null): void {
    this.profileImageService
      .resolve(key)
      .subscribe((url) => (this.profileImageDisplayUrl = url));
  }

  // Greift, wenn die presigned URL abgelaufen ist oder die Datei im Bucket fehlt - MinIO
  // signiert auch nicht existierende Objekte anstandslos, der Fehler zeigt sich erst hier.
  onImageError(): void {
    // Ohne diese Prüfung entstünde eine Endlosschleife, falls auch das Asset fehlt
    if (this.profileImageDisplayUrl !== DEFAULT_AVATAR) {
      this.profileImageDisplayUrl = DEFAULT_AVATAR;
    }
  }

  // Übernimmt die Antwort des schmalen Endpunkts. residences wird gegen null abgesichert,
  // weil der Dialog ungeprüft residences[0] liest.
  private applyHeaderInfo(info: UserHeaderInfo): void {
    this.userData = {
      ...this.userData,
      userId: info.userId,
      firstname: info.firstName,
      lastname: info.lastName,
      email: info.email,
      profileImageUrl: info.profileImageUrl,
      notifications: info.notifications,
      residences: info.residences ?? [],
    };
    this.userDataLoaded = true;
  }

  // Ohne bekannten Namen entfällt der Eintrag, damit dort kein "undefined undefined" steht;
  // Logout bleibt dadurch in jedem Fall erreichbar.
  private buildProfileMenu(displayName: string | null): void {
    this.profileMenuItems = displayName
      ? [
          { label: displayName, disabled: true },
          { separator: true },
          ...this.profileMenuItemsConst,
        ]
      : [...this.profileMenuItemsConst];
  }

  private buildSettingsMenu(): MenuItem[] {
    const entries: SettingsMenuEntry[] = [
      {
        label: 'Benutzer:innen',
        icon: 'pi pi-users',
        routerLink: '/user',
        visible: this.authService.isAdmin,
      },
      {
        label: 'Dozentenplanung importieren',
        icon: 'pi pi-file-import',
        routerLink: '/import',
        permission: 'Scheduler.Write',
      },
      {
        label: 'Aufzeichnungen',
        icon: 'pi pi-video',
        routerLink: '/recordings',
        permission: 'Recordings.Write',
      },
      {
        label: 'Rollen & Berechtigungen',
        icon: 'pi pi-shield',
        routerLink: '/roles',
        permission: 'Roles.Write',
      },
      {
        label: 'Workflows',
        icon: 'pi pi-sitemap',
        routerLink: '/workflows',
        permission: 'Workflow.Read',
      },
      {
        label: 'Inventarliste',
        icon: 'pi pi-desktop',
        routerLink: '/inventory',
        permission: INVENTORY_PERMISSIONS.read,
      },
      {
        label: 'Reports',
        icon: 'pi pi-flag',
        routerLink: '/reports',
        permission: 'Reports.Read',
      },
      {
        label: 'Teams / Zoom Konfiguration',
        icon: 'pi pi-video',
        routerLink: '/teams-zoom-config',
        // Reiner Verwaltungsbereich; muss mit teamsUserGuard übereinstimmen.
        visible: mayAdministerMeetings(this.authService),
      },
      {
        label: 'Anwesenheiten (Live)',
        icon: 'pi pi-circle-on',
        routerLink: '/live-attendance',
        permission: 'Meeting.User.Read',
      },
      {
        label: 'Einstellungen',
        icon: 'pi pi-cog',
        routerLink: '/settings',
      },
      {
        label: 'Krankmeldungen',
        icon: 'pi pi-file-check',
        routerLink: '/absence-notifications',
        permission: 'Absence.Write',
      },
      {
        label: 'PDF-Signierung',
        icon: 'pi pi-file-edit',
        routerLink: '/pdf-signer',
        permission: 'Pdf.Sign',
      },
      {
        // Dieselbe Route wie 'Mein Stundenplan' im Profilmenü; hier zur Auswahl
        // eines fremden Dozentenplans.
        label: 'Dozenten-Stundenpläne',
        icon: 'pi pi-calendar',
        routerLink: '/docent-timetable',
        visible: this.authService.isAdmin,
      },
    ];

    return entries.map(({ label, icon, routerLink, permission, visible }) => ({
      label,
      icon,
      routerLink,
      visible: visible ?? (!permission || this.hasPermission(permission)),
    }));
  }

  private hasPermission(permission: string): boolean {
    return this.permissions.includes(permission);
  }

  // Kein Nachladen: alles, was der Dialog braucht, kam bereits beim Init mit.
  showRegisterDialog(): void {
    this.displayRegisterDialog = true;
  }

  // Der Editor hat den neuen Object-Key bereits gespeichert; hier wird nur noch die
  // Anzeige nachgezogen - auch die des kleinen Avatars in der Navbar.
  onProfileImageChanged(key: string | null): void {
    this.userData = { ...this.userData, profileImageUrl: key ?? undefined };
    this.resolveProfileImage(key);
  }

  openProfileMenu(event: Event): void {
    this.profileMenu.toggle(event);
  }

  navigateToDashboard(): void {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/dashboard']);
    } else {
      this.router.navigate(['/login']);
    }
  }

  logout(): void {
    this.activityService.clearActivityCache();
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  // Speichert die geänderten Daten und lädt sie anschließend frisch nach; der Dialog
  // schließt erst, wenn der Server den Vorgang bestätigt hat.
  handleDialogClose(event: UpdatePersonalInformationDTO): void {
    if (this.savingUserData) return;

    this.savingUserData = true;

    this.userService
      .updatePersonalInformation(event)
      .pipe(
        switchMap(() => this.userService.getUserById(event.id)),
        finalize(() => (this.savingUserData = false)),
      )
      .subscribe({
        next: (info) => {
          this.applyHeaderInfo(info);
          // Nicht aus dem JWT: der Token trägt nach einer Namensänderung bis zum
          // nächsten Login noch den alten Namen.
          this.buildProfileMenu(`${info.firstName} ${info.lastName}`);
          this.resolveProfileImage(info.profileImageUrl);
          this.displayRegisterDialog = false;
          this.notificationService.success('Ihre Daten wurden aktualisiert.');
        },
        error: (err) =>
          this.notificationService.error(
            // 409: E-Mail-Adresse wird schon von jemand anderem als Login verwendet
            err?.status === 409 && err.error?.message
              ? err.error.message
              : 'Daten konnten nicht gespeichert werden.',
            err,
          ),
      });
  }
}
